/**
 * DiagnosticDiagram — Interactive SVG flowchart for troubleshooting.
 * Use case: RMA / Returns — Hardware diagnostics (e.g., device not powering on).
 */
import React, { useState } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';

// Simple flowchart: Power → Cable → Outlet → Device
const DEFAULT_STEPS = [
  { id: 'power', label: 'Device powers on?', yes: 'resolved', no: 'cable' },
  { id: 'cable', label: 'Cable firmly connected?', yes: 'outlet', no: 'resolved' },
  { id: 'outlet', label: 'Outlet working?', yes: 'device', no: 'resolved' },
  { id: 'device', label: 'Try different outlet', yes: 'resolved', no: 'rma' },
  { id: 'rma', label: 'Initiate RMA', yes: 'resolved', no: null },
  { id: 'resolved', label: 'Issue resolved', yes: null, no: null },
];

export default function DiagnosticDiagram({
  steps = DEFAULT_STEPS,
  value,
  onChange,
  label = 'Hardware diagnostic',
  ...props
}) {
  const [currentStep, setCurrentStep] = useState(value?.currentStep || 'power');
  const [path, setPath] = useState(value?.path || []);

  const step = steps.find((s) => s.id === currentStep);

  const handleAnswer = (ans) => {
    const nextId = ans === 'yes' ? step?.yes : step?.no;
    const nextPath = [...path, { step: currentStep, answer: ans }];
    setPath(nextPath);
    if (nextId === 'resolved' || !nextId) {
      setCurrentStep('resolved');
      onChange?.({ target: { value: { currentStep: 'resolved', path: nextPath, outcome: nextId || 'resolved' } } });
    } else if (nextId === 'rma') {
      setCurrentStep('rma');
      onChange?.({ target: { value: { currentStep: 'rma', path: nextPath, outcome: 'rma' } } });
    } else {
      setCurrentStep(nextId);
      onChange?.({ target: { value: { currentStep: nextId, path: nextPath } } });
    }
  };

  const isResolved = currentStep === 'resolved' || currentStep === 'rma';

  return (
    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }} {...props}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      {!isResolved && step && (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {step.label}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="success" size="small" onClick={() => handleAnswer('yes')}>
              Yes
            </Button>
            <Button variant="outlined" color="primary" size="small" onClick={() => handleAnswer('no')}>
              No
            </Button>
          </Stack>
        </>
      )}
      {isResolved && (
        <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
          {currentStep === 'rma' ? 'RMA recommended — please submit return request.' : 'Diagnostic complete.'}
        </Typography>
      )}
      {path.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Path: {path.map((p) => `${p.step}=${p.answer}`).join(' → ')}
        </Typography>
      )}
    </Box>
  );
}
